/**
 * The audit log's lines for scheduled events (Discord's 100, 101, 102). The
 * event's name is the one the entry stored (`options.nx_event_name`, or the
 * `name` change), so a deleted event still reads by name; a status change
 * reads as what it did: started, ended, cancelled.
 */
import {
  AuditLogEvent,
  GuildScheduledEventStatus as ST,
  type AuditLogEntry,
} from '@neosocial/protocol';
import type { Translator } from '../i18n';

export const EVENT_AUDIT_FILTERS: Array<{ key: string; type: number }> = [
  { key: 'ev.audit.f.create', type: AuditLogEvent.GUILD_SCHEDULED_EVENT_CREATE },
  { key: 'ev.audit.f.update', type: AuditLogEvent.GUILD_SCHEDULED_EVENT_UPDATE },
  { key: 'ev.audit.f.delete', type: AuditLogEvent.GUILD_SCHEDULED_EVENT_DELETE },
];

function nameOf(e: AuditLogEntry): string {
  const c = e.changes?.find((x) => x.key === 'name');
  const v = e.options?.nx_event_name ?? c?.new_value ?? c?.old_value;
  return typeof v === 'string' && v ? v : '—';
}

export function eventAuditLine(tr: Translator, e: AuditLogEntry, actor: string): string {
  const name = nameOf(e);
  switch (e.action_type as number) {
    case AuditLogEvent.GUILD_SCHEDULED_EVENT_CREATE:
      return tr.t('ev.audit.create', { actor, name });
    case AuditLogEvent.GUILD_SCHEDULED_EVENT_DELETE:
      return tr.t('ev.audit.delete', { actor, name });
    default: {
      const status = e.changes?.find((x) => x.key === 'status')?.new_value;
      if (status === ST.ACTIVE) return tr.t('ev.audit.start', { actor, name });
      if (status === ST.COMPLETED) return tr.t('ev.audit.end', { actor, name });
      if (status === ST.CANCELED) return tr.t('ev.audit.cancel', { actor, name });
      return tr.t('ev.audit.update', { actor, name });
    }
  }
}
